import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

const backendApi = config.backendApi;

/**
 * The wire name of a quantity, which is deliberately not the name of the metric
 * it came from: `interface_hc_*` was retired out from under a namespace that had
 * leaked counter width to callers, and these names survived that.
 */
export type TrafficQuantity =
  | 'bits_in'
  | 'bits_out'
  | 'packets_in'
  | 'packets_out'
  | 'errors_in'
  | 'errors_out'
  | 'discards_in'
  | 'discards_out'
  | 'nonunicast_in';

/**
 * Why there is or is not a denominator. Four states rather than a nullable
 * number, because "no number" means three different things and a reader renders
 * each differently — a down port is an *answer*, a bridge makes the question a
 * category error, and only `unknown` is a gap.
 */
export type CapacityState = 'known' | 'link_down' | 'no_physical_rate' | 'unknown';

export interface QuantitySeries {
  quantity: TrafficQuantity;
  unit: 'bits_per_second' | 'packets_per_second';
  /**
   * How much the counter advanced across the window, from its own first and
   * last readings rather than from the rated points below.
   *
   * `null` on a device-wide fetch, which does not pay for the endpoint reads,
   * and `null` when the counter reset — two points can say a reset happened but
   * not how much was lost before it.
   */
  total: number | null;
  resetInRange: boolean;
  /**
   * `[epoch_millis, value]`, ascending. A `null` is a bucket the store knows
   * nothing about — a poll that never arrived, or an interval whose only
   * reading was a counter reset the query filtered out. It is not zero.
   */
  points: [number, number | null][];
}

export interface CapacityPoint {
  ts: number;
  /** Non-null exactly when `state` is `known`. */
  bits: number | null;
  state: CapacityState;
}

export interface StatusTransition {
  ts: number;
  /** RFC 2863 `ifOperStatus` names. */
  from: string;
  to: string;
}

/**
 * The rule table's answer for one port, evaluated server-side so the sentence
 * and the tiles beneath it come from one reading of one set of series.
 *
 * Every figure is a **peak rate**, never a total: the series arrive already
 * rated and bucketed with `max`, and a count cannot be integrated back out of
 * that without inventing one.
 */
export interface InterfaceVerdict {
  rule: string;
  sentence: string;
  severity: 'critical' | 'warning' | 'none';
  peakBitsIn: number | null;
  peakBitsOut: number | null;
  peakUtilization: number | null;
  peakErrorsIn: number | null;
  peakErrorsOut: number | null;
  peakDiscardsIn: number | null;
  peakDiscardsOut: number | null;
  /** Exact counts, present on a single-interface fetch. */
  totalErrorsIn: number | null;
  totalErrorsOut: number | null;
  totalDiscardsIn: number | null;
  totalDiscardsOut: number | null;
  nonUnicastShare: number | null;
  operStatus: string | null;
  flaps: number;
}

export interface InterfaceTraffic {
  /** The interface (`device_interfaces.id`): the join key (gateway#102). */
  interfaceId: string;
  /** Its name, which its series are found by. */
  interfaceName: string;
  /** The ifIndex it carries now, an attribute. */
  ifindex: string | null;
  series: QuantitySeries[];
  /** Aligned to the timestamps of the first series by construction. */
  capacity: CapacityPoint[];
  transitions: StatusTransition[];
  /**
   * State entering the window. `null` means nothing was published early enough
   * to say — the only case where an empty `transitions` means "we do not know"
   * rather than "nothing changed".
   */
  statusBefore: string | null;
  verdict: InterfaceVerdict;
}

export interface DeviceTrafficResponse {
  start: number;
  end: number;
  /** `null` means raw samples rather than buckets. */
  bucketSeconds: number | null;
  bucketFunction: string | null;
  interfaces: InterfaceTraffic[];
}

export const fetchDeviceTrafficSeries = async (
  deviceId: string,
  start: number,
  end: number,
  interfaceId?: string,
): Promise<DeviceTrafficResponse> => {
  const params = new URLSearchParams({ start: String(start), end: String(end) });
  if (interfaceId !== undefined) params.set('interface_id', interfaceId);
  const response = await axios.get<DeviceTrafficResponse>(
    `${backendApi}/api/devices/${deviceId}/traffic-series?${params.toString()}`,
    { withCredentials: true },
  );
  return response.data;
};

/**
 * The cache key for one device's traffic, or one interface's. Shared so every
 * surface that reads the same window of the same port reads one cache entry.
 */
export const deviceTrafficQueryKey = (
  deviceId: string | undefined,
  bounds: { start: number; end: number } | null,
  interfaceId?: string,
) => ['device-traffic', deviceId, bounds, interfaceId ?? null] as const;

/**
 * One device's per-interface traffic over a window.
 *
 * Every interface arrives in one response, deliberately: the surfaces that
 * consume this rank ports against each other, so a per-port endpoint would make
 * "which port is worst" a client-side fan-out over 125 requests. A panel that
 * wants one port takes one out of the list — and pays nothing for the second
 * port the reader opens, because the response is already cached under the
 * device.
 */
export function useDeviceTrafficSeries(
  deviceId: string | undefined,
  bounds: { start: number; end: number } | null,
  /**
   * Narrow to one interface, by id. A panel about one port should pass it: measured on
   * the dev router, 84 KB and 584 ms device-wide against 5.3 KB and 54 ms for
   * one interface — and narrowing is also what makes the exact counter totals
   * affordable, so the verdict can say "5,495 frames" instead of "peak 12.6/s".
   */
  interfaceId?: string,
) {
  const contextReady = useActiveContextReady();

  return useQuery<DeviceTrafficResponse>({
    queryKey: deviceTrafficQueryKey(deviceId, bounds, interfaceId),
    queryFn: () => {
      if (!deviceId || !bounds) {
        throw new Error('deviceId and a window are required to fetch traffic series.');
      }
      return fetchDeviceTrafficSeries(deviceId, bounds.start, bounds.end, interfaceId);
    },
    enabled: !!deviceId && !!bounds && contextReady,
    // The window's end advances on a timer rather than on every render, so a
    // key change here always means a genuinely newer window.
    staleTime: 30_000,
  });
}
